import { useCallback, useEffect, useMemo, useState } from "react";
import { api, post } from "./api";
import type {
  AndroidIncident, AndroidIncidentRecommendation, AppInfo, CodexModels,
} from "./types";


const activeStatuses = new Set(["queued", "capturing", "analyzing"]);

const statusCopy: Record<AndroidIncident["status"], string> = {
  queued: "Queued",
  capturing: "Collecting Android evidence",
  analyzing: "Android expert is analyzing evidence",
  completed: "Analysis complete",
  failed: "Analysis failed",
};

function packageShortName(packageName: string) {
  const parts = packageName.split(".");
  return parts.at(-1) || packageName;
}

function downloadJson(filename: string, value: unknown) {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function AndroidExpertPanel({
  codexModels, modelBusy, foregroundPackage, onSelectModel, onNotice,
}: {
  codexModels?: CodexModels;
  modelBusy: boolean;
  foregroundPackage?: string;
  onSelectModel: (model: string, role: "expert") => Promise<void>;
  onNotice: (message: string | undefined) => void;
}) {
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [incidents, setIncidents] = useState<AndroidIncident[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [packageName, setPackageName] = useState("");
  const [question, setQuestion] = useState(
    "Determine why this Android app closes shortly after opening and recommend safe fixes."
  );
  const [observationSeconds, setObservationSeconds] = useState(45);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [appResult, incidentResult] = await Promise.all([
        api<{ apps: AppInfo[] }>("/api/apps"),
        api<{ incidents: AndroidIncident[] }>("/agent/android-incidents?limit=50"),
      ]);
      setApps(appResult.apps);
      setIncidents(incidentResult.incidents);
      setSelectedId((current) => current || incidentResult.incidents[0]?.id);
      setPackageName((current) => {
        if (current) return current;
        const previous = incidentResult.incidents[0]?.package_name;
        if (previous) return previous;
        if (foregroundPackage && appResult.apps.some((item) => item.packageName === foregroundPackage)) {
          return foregroundPackage;
        }
        return appResult.apps.find((item) => !item.system)?.packageName || "";
      });
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    }
  }, [foregroundPackage, onNotice]);

  useEffect(() => { void load(); }, [load]);
  const hasActive = incidents.some((incident) => activeStatuses.has(incident.status));
  useEffect(() => {
    if (!hasActive) return;
    const timer = window.setInterval(() => void load(), 2_500);
    return () => window.clearInterval(timer);
  }, [hasActive, load]);

  const selected = useMemo(
    () => incidents.find((incident) => incident.id === selectedId) || incidents[0],
    [incidents, selectedId],
  );
  const selectedExit = selected?.evidence?.primaryExit || selected?.evidence?.latestExit;

  const create = async (reproduce: boolean) => {
    if (!packageName || !question.trim()) return;
    if (reproduce && !window.confirm(
      `Reproduce the exit for ${packageName}? This will open the app and observe its process for up to ${observationSeconds} seconds.`
    )) return;
    setCreating(true);
    onNotice(undefined);
    try {
      const incident = await post<AndroidIncident>("/agent/android-incidents", {
        packageName,
        question: question.trim(),
        reproduce,
        observationSeconds,
      });
      setIncidents((current) => [incident, ...current]);
      setSelectedId(incident.id);
      onNotice(reproduce
        ? `Reproducing and analyzing ${packageName}.`
        : `Analyzing the recent Android exit history for ${packageName}.`);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    } finally {
      setCreating(false);
    }
  };

  const deviceAction = async (action: AndroidIncidentRecommendation["action"]) => {
    if (!selected) return;
    const target = selected.package_name;
    try {
      if (action === "clear-app-data") {
        const confirmation = window.prompt(
          `Clearing data permanently removes this app's local files and login state. Type ${target} to continue.`
        );
        if (confirmation !== target) return;
        await post("/api/clear-app-data", { packageName: target });
        onNotice(`Local data cleared for ${target}. Reopen it and run a new diagnosis.`);
      } else if (action === "relaunch") {
        await post("/api/launch", { packageName: target });
        onNotice(`${target} relaunched. The live screen remains under your control.`);
      } else if (action === "open-app-settings") {
        await post("/api/app-details", { packageName: target });
        onNotice(`Opened Android app details for ${target}.`);
      }
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    }
  };

  const remove = async (incident: AndroidIncident) => {
    if (!window.confirm(`Delete this diagnostic report for ${incident.package_name}?`)) return;
    try {
      await api(`/agent/android-incidents/${incident.id}`, { method: "DELETE" });
      setIncidents((current) => current.filter((item) => item.id !== incident.id));
      setSelectedId(undefined);
    } catch (error) {
      onNotice(error instanceof Error ? error.message : String(error));
    }
  };

  return <div className="panel-content android-expert">
    <div className="section-title">
      <div><span className="eyebrow">Package-scoped incident analysis</span><h2>Android Expert</h2></div>
      <span className="expert-boundary">Read-only AI</span>
    </div>

    <section className="expert-request">
      <label><span>Installed app</span><select aria-label="App to diagnose" value={packageName} onChange={(event) => setPackageName(event.target.value)}>
        {!apps.length && <option value="">No third-party apps found</option>}
        {apps.map((app) => <option key={app.packageName} value={app.packageName}>{packageShortName(app.packageName)} · {app.packageName}</option>)}
      </select></label>
      <label><span>Expert model</span><select data-role="expert" aria-label="Android expert model" value={codexModels?.expertSelected || ""} disabled={modelBusy || !codexModels?.models.length} onChange={(event) => void onSelectModel(event.target.value, "expert")}>
        {!codexModels?.models.length && <option value="">Unavailable</option>}
        {codexModels?.models.map((model) => <option key={model.id} value={model.id}>{model.label}{model.isDefault ? " · default" : ""}</option>)}
      </select></label>
      <label><span>Diagnostic question</span><textarea value={question} onChange={(event) => setQuestion(event.target.value)} rows={3} maxLength={2000} /></label>
      <label><span>Reproduction window</span><select aria-label="Reproduction window" value={observationSeconds} onChange={(event) => setObservationSeconds(Number(event.target.value))}>
        <option value={15}>15 seconds</option><option value={30}>30 seconds</option><option value={45}>45 seconds</option><option value={60}>60 seconds</option>
      </select></label>
      <div className="expert-run-actions">
        <button disabled={creating || !packageName || !question.trim()} onClick={() => void create(false)}>Analyze recent exit</button>
        <button className="reproduce" disabled={creating || !packageName || !question.trim()} onClick={() => void create(true)}>Reproduce & analyze</button>
      </div>
      <small>Recent-exit analysis does not open the app. Reproduction is explicit, launches it once, and never clears data or changes Android settings.</small>
    </section>

    {incidents.length > 0 && <div className="incident-picker">
      <label>Diagnostic history</label>
      <select value={selected?.id || ""} onChange={(event) => setSelectedId(event.target.value)}>
        {incidents.map((incident) => <option key={incident.id} value={incident.id}>
          {new Date(incident.created_at * 1000).toLocaleString()} · {packageShortName(incident.package_name)} · {incident.status}
        </option>)}
      </select>
    </div>}

    {!selected && <div className="empty-state compact"><span>⌕</span><strong>No diagnostic reports yet</strong><p>Select an app and analyze its recent Android exit history.</p></div>}

    {selected && <section className="incident-report">
      <div className="incident-heading">
        <div><strong>{selected.package_name}</strong><small>{new Date(selected.created_at * 1000).toLocaleString()} · {selected.reproduce ? "Reproduced" : "Existing evidence"}</small></div>
        <span className={`task-state state-${selected.status}`}>{selected.status}</span>
      </div>

      {activeStatuses.has(selected.status) && <div className="expert-progress"><span className="expert-spinner" /><div><strong>{statusCopy[selected.status]}</strong><small>Evidence and the final report will remain in diagnostic history.</small></div></div>}
      {selected.error && <div className="expert-error"><strong>Analysis did not complete</strong><span>{selected.error}</span>{selected.evidence && <small>The captured Android evidence is still available below.</small>}</div>}

      {selected.report && <>
        <div className="expert-verdict"><div><span>{selected.report.verdict.replaceAll("-", " ")}</span><strong>{Math.round(selected.report.confidence * 100)}% confidence</strong></div><p>{selected.report.summary}</p></div>
        <article className="root-cause"><span>{selected.report.rootCause.category}</span><h3>{selected.report.rootCause.title}</h3><p>{selected.report.rootCause.explanation}</p><ul>{selected.report.rootCause.evidence.map((item, index) => <li key={index}>{item}</li>)}</ul></article>
        {selected.report.findings.length > 0 && <div className="expert-findings"><h3>Findings</h3>{selected.report.findings.map((finding, index) => <article key={index} data-severity={finding.severity}><div><strong>{finding.title}</strong><span>{finding.severity}</span></div><code>{finding.evidence}</code><p>{finding.interpretation}</p></article>)}</div>}
        {selected.report.recommendedActions.length > 0 && <div className="expert-remediations"><h3>Recommended next steps</h3>{selected.report.recommendedActions.map((recommendation, index) => <article key={index}>
          <div><strong>{index + 1}. {recommendation.title}</strong><span className={`risk-${recommendation.risk}`}>{recommendation.risk} risk</span></div>
          <p>{recommendation.description}</p>
          {recommendation.action === "relaunch" && <button onClick={() => void deviceAction("relaunch")}>Relaunch app</button>}
          {recommendation.action === "open-app-settings" && <button onClick={() => void deviceAction("open-app-settings")}>Open app settings</button>}
          {recommendation.action === "clear-app-data" && <button className="danger" onClick={() => void deviceAction("clear-app-data")}>Clear app data…</button>}
        </article>)}</div>}
        {selected.report.limitations.length > 0 && <details className="expert-limitations"><summary>Analysis limitations</summary><ul>{selected.report.limitations.map((item, index) => <li key={index}>{item}</li>)}</ul></details>}
      </>}

      {selected.evidence && <details className="expert-evidence">
        <summary>Android evidence <span>{selected.evidence.signals?.length || 0} signals · {selected.evidence.exits?.length || 0} exits</span></summary>
        <div className="evidence-facts">
          <label>Primary mechanism</label><strong>{selectedExit?.reason || "No exit record"}{selectedExit?.status !== undefined ? ` · status ${selectedExit.status}` : ""}</strong>
          <label>Memory at exit</label><strong>{selectedExit?.pss || "?"} PSS · {selectedExit?.rss || "?"} RSS</strong>
          <label>App version</label><strong>{selected.evidence.package?.versionName || "?"} ({selected.evidence.package?.versionCode ?? "?"})</strong>
          <label>Reproduction</label><strong>{selected.evidence.reproduction?.outcome || "not run"}</strong>
        </div>
        <div className="signal-list">{selected.evidence.signals?.map((signal) => <span key={signal.signal}>{signal.signal} · {signal.count}</span>)}</div>
        <details><summary>Filtered logcat</summary><pre>{selected.evidence.logcat || "No package-specific log lines captured."}</pre></details>
        <details><summary>Raw ApplicationExitInfo</summary><pre>{selected.evidence.rawExitInfo || "No exit information captured."}</pre></details>
        <small>{selected.evidence.privacy?.note}</small>
      </details>}

      <div className="incident-actions">
        <button onClick={() => void deviceAction("relaunch")}>Relaunch</button>
        <button onClick={() => void deviceAction("open-app-settings")}>App settings</button>
        {selected.evidence && <button onClick={() => downloadJson(`is4f-${selected.package_name}-diagnostic.json`, selected)}>Export JSON</button>}
        <button className="danger" disabled={activeStatuses.has(selected.status)} onClick={() => void remove(selected)}>Delete report</button>
      </div>
      {selected.model && <small className="expert-model-used">Analyzed by {selected.model}</small>}
    </section>}
  </div>;
}
